import { Box, Checkbox, FormControlLabel, ToggleButton, ToggleButtonGroup, useTheme } from '@mui/material';
import { t } from 'i18next';
import { useCollectionMeta, useJsonAPI } from 'json-api-hooks';
import React, { FC, createContext, useContext, useMemo, useState } from 'react';

import CollectionTable from '../../components/./Collection/CollectionTable';
import { CollectionContext } from '../../components/Collection/Collection';
import ContainerHeader from '../../components/Container/ContainerHeader';
import { Column } from '../../components/Table';
import { useCurrentActor } from '../../hooks/useCurrentActor';
import useModalDispatch from '../../hooks/useModalDispatch';
import AddressModal from '../../modals/AddressModal';
import SignInModal from '../../modals/SignInModal';
import { AddressResource } from '../../models';

import AddressBulkForm from './AddressBulkForm';

const AddressContext = createContext<{
  bulkEnabled: boolean;
  selected: string[];
  toggle: (address: AddressResource) => void;
}>({} as any);

const columns: Column<'addresses'>[] = [
  {
    label: t('table.addresses.columns.address'),
    attribute: 'number',
    render: ({ attributes: { name }, id }) => {
      const { bulkEnabled, selected } = useContext(AddressContext);

      return (
        <React.Fragment>
          {bulkEnabled && (
            <Checkbox
              checked={selected.includes(id)}
              color="primary"
              sx={{
                padding: 0,
                paddingRight: 3,
              }}
            />
          )}
          {name}
        </React.Fragment>
      );
    },
    sortable: true,
  },
];

const AddressTable: FC<{ setPending: (pending: boolean) => void; pending: boolean }> = ({ pending, setPending }) => {
  const { router } = useJsonAPI();
  const theme = useTheme();
  const dispatchSignInModal = useModalDispatch(SignInModal);
  const dispatchAddressModal = useModalDispatch(AddressModal);
  const { actorType } = useCurrentActor();
  const [bulkEnabled, setBulkEnabled] = useState(false);
  const [selected, setSelected] = useState<string[]>([]);
  const value = useMemo(
    () => ({
      bulkEnabled,
      selected,
      toggle: (address: AddressResource) =>
        setSelected((prev) => {
          if (prev.includes(address.id)) {
            return prev.filter((val) => val !== address.id);
          }

          return [...prev, address.id];
        }),
    }),
    [selected, bulkEnabled],
  );
  const { collectionOpts, resourceType } = useContext(CollectionContext);
  const { totalCount } = useCollectionMeta(resourceType, collectionOpts);

  const onRowClick = (address: AddressResource) => {
    if (bulkEnabled) {
      value.toggle(address);
    } else {
      if (actorType === 'GuestUser') {
        dispatchSignInModal({ redirectUrl: router.resourceRoute(address) });
      } else {
        dispatchAddressModal({ address });
      }
    }
  };

  return (
    <AddressContext.Provider value={value}>
      <ContainerHeader
        float={
          <>
            {pending ? (
              <FormControlLabel
                control={
                  <Checkbox checked={bulkEnabled} color="primary" onClick={() => setBulkEnabled(!bulkEnabled)} />
                }
                label="Selecteer meerdere"
                sx={{ marginRight: 0, flexShrink: 100 }}
              />
            ) : (
              <div />
            )}
            <ToggleButtonGroup
              exclusive
              sx={{ width: { xs: '100%', sm: 'auto' } }}
              value={pending}
              onChange={(_event, newPending) => {
                if (typeof newPending === 'boolean') {
                  setPending(newPending);
                }
              }}
            >
              <ToggleButton size="small" sx={{ flexGrow: { xs: 1, sm: 0 }, flexShrink: 0 }} value={true}>
                Nog te doen
              </ToggleButton>
              <ToggleButton size="small" sx={{ flexGrow: { xs: 1, sm: 0 }, flexShrink: 0 }} value={false}>
                Al bezocht
              </ToggleButton>
            </ToggleButtonGroup>
          </>
        }
        title="Adressen"
      />
      {!pending && totalCount ? (
        <Box sx={{ color: theme.palette.grey.midDark }}>
          Je kan ingevoerde gegevens niet meer bewerken omdat ze direct geanonimiseerd worden, maar je kan wel een extra
          formulier indienen.
        </Box>
      ) : undefined}
      <CollectionTable<'addresses'>
        columns={columns}
        onEmpty={() =>
          pending
            ? 'Alle adressen in deze straat zijn al bezocht!'
            : 'Er zijn nog geen adressen in deze straat bezocht.'
        }
        onRowClick={onRowClick}
      />
      {selected.length > 0 && bulkEnabled && <AddressBulkForm selected={selected} setSelected={setSelected} />}
    </AddressContext.Provider>
  );
};

export default AddressTable;
